/**
 * form-fields.js
 *
 * Responsible for:
 *  - Attaching `input` / `blur` / `change` listeners to every form field
 *    defined in FIELD_CONFIG (formatting + inline validation).
 *  - Exposing the `setError` helper used by the submit handler.
 *  - Returning a map of resolved DOM elements for later use.
 */

import { FIELD_CONFIG } from './field-config';
import { saveFormData } from './form-persistence';

/**
 * Set or clear an error message on the closest `.cr-field` / `.cr-checkbox`
 * ancestor of the given element.
 *
 * @param {HTMLElement} el  - The input / checkbox that triggered the error.
 * @param {string}      msg - Error text; empty string clears the error.
 */
export function setError(el, msg) {
	const field = el.closest('.cc-field, .cr-checkbox');
	if (!field) return;

	const errorEl = field.querySelector('.cc-field__error-msg');
	if (errorEl) errorEl.textContent = msg;

	field.classList.toggle('cc-field--error', !!msg);
	field.classList.toggle('cr-checkbox--error', !!msg);
}

/**
 * Toggle the global form loader overlay.
 *
 * @param {HTMLFormElement} form
 * @param {boolean}         isLoading
 */
export function toggleLoader(form, isLoading) {
	const loader = form.querySelector('.cr-loader');
	if (loader) {
		loader.classList.toggle('active', isLoading);
	}
}

/**
 * Wire up all field listeners defined in FIELD_CONFIG.
 *
 * @param {HTMLFormElement} form
 * @returns {Record<string, HTMLElement>} Map of field name → DOM element.
 */
export function initFormFields(form) {
	const elements = {};

	Object.entries(FIELD_CONFIG).forEach(([name, config]) => {
		const el = form.querySelector(config.selector);
		if (!el) return;
		elements[name] = el;

		if (config.type === 'input') {
			el.addEventListener('input', (e) => {
				if (config.format) {
					e.target.value = config.format(e.target.value);
				}
				setError(el, ''); // clear error as soon as user starts typing
				saveFormData(elements);
			});

			el.addEventListener('blur', (e) => {
				if (config.validate) {
					setError(el, config.validate(e.target.value));
				}
			});
		} else if (config.type === 'checkbox') {
			el.addEventListener('change', () => {
				setError(el, ''); // clear on change
				saveFormData(elements);
			});
		}
	});

	return elements;
}

/**
 * Centralized helper to manage interactivity across all slides.
 * Disables all focusable elements in inactive slides.
 *
 * @param {HTMLFormElement} form
 * @param {import('swiper').default} swiper
 */
export function updateSlideInteractivity(form, swiper) {
	if (!swiper) return;

	const slides = form.querySelectorAll('.swiper-slide');
	const activeIndex = swiper.activeIndex;

	slides.forEach((slide, index) => {
		const isActive = index === activeIndex;
		const focusableElements = slide.querySelectorAll(
			'input, button, select, textarea'
		);

		focusableElements.forEach((el) => {
			// If it's not the active slide, definitely disable it.
			if (!isActive) {
				el.disabled = true;
			} else {
				// If it IS the active slide, we might enable it.
				// However, Slide 1 has its own complex state (card-check).
				// We only enable if it's not the card-check fields (handled separately)
				// or if it's a field we generally want enabled on slide 1.
				if (index === 0) {
					// Slide 1 logic:
					// Card number is always enabled.
					if (el.id === 'cr-card-number') {
						el.disabled = false;
					}
					// Note: form.recheckCard() will be called after this loop 
					// to handle the rest of Slide 1 fields.
				} else {
					// Other slides (Slide 2): all elements enabled once slide is active.
					el.disabled = false;
				}
			}
		});
	});

	// If we just entered Slide 1, let card-check.js re-evaluate field states.
	if (activeIndex === 0 && typeof form.recheckCard === 'function') {
		form.recheckCard();
	}
}
